import React, { useState, useEffect } from 'react';
import { observabilityService } from '../services/observability-service';

interface WorkflowResource {
  arn: string;
  name: string;
  type: 'StateMachine' | 'Lambda' | 'DynamoDB' | 'LogGroup';
  workflowType?: string;
  function?: string;
  tags: Record<string, string>;
  status: 'Active' | 'Inactive' | 'Unknown';
  metrics?: {
    invocations?: number;
    errors?: number;
    duration?: number;
    lastExecution?: string;
    cost?: number;
  };
}

interface WorkflowResourceGroup {
  workflowType: string;
  resources: WorkflowResource[];
  totalResources: number;
  activeResources: number;
  totalCost?: number;
  healthStatus: 'Healthy' | 'Warning' | 'Critical';
}

const WorkflowResources: React.FC = () => {
  const [resourceGroups, setResourceGroups] = useState<WorkflowResourceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'groups' | 'list'>('groups');

  useEffect(() => {
    loadResourceGroups();
  }, []);

  const loadResourceGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/resources/groups', {
        headers: await getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      setResourceGroups(result.data.resourceGroups || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow resources');
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = async () => {
    // This should use your existing auth service
    return {
      'Content-Type': 'application/json',
      // Add authorization header here
    };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'text-green-600 bg-green-100';
      case 'Inactive': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'Healthy': return 'text-green-600 bg-green-100';
      case 'Warning': return 'text-yellow-600 bg-yellow-100';
      case 'Critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'StateMachine': return '🔄';
      case 'Lambda': return '⚡';
      case 'DynamoDB': return '🗄️';
      case 'LogGroup': return '📝';
      default: return '📦';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading resources</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <button
              onClick={loadResourceGroups}
              className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workflow Resources</h2>
          <p className="text-gray-600">Monitor and manage all workflow-related AWS resources</p>
        </div>
        
        <div className="flex space-x-4">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('groups')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'groups'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Groups
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              List
            </button>
          </div>
          
          <button
            onClick={loadResourceGroups}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">📊</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Groups</p>
              <p className="text-2xl font-semibold text-gray-900">{resourceGroups.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🔧</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Resources</p>
              <p className="text-2xl font-semibold text-gray-900">
                {resourceGroups.reduce((sum, g) => sum + g.totalResources, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Resources</p>
              <p className="text-2xl font-semibold text-gray-900">
                {resourceGroups.reduce((sum, g) => sum + g.activeResources, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">💰</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Est. Cost</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${resourceGroups.reduce((sum, g) => sum + (g.totalCost || 0), 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Resource Groups */}
      {viewMode === 'groups' && (
        <div className="space-y-6">
          {resourceGroups.map((group) => (
            <div key={group.workflowType} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      {group.workflowType}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getHealthColor(group.healthStatus)}`}>
                      {group.healthStatus}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{group.totalResources} resources</span>
                    <span>{group.activeResources} active</span>
                    {group.totalCost && <span>${group.totalCost.toFixed(2)}</span>}
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.resources.map((resource) => (
                    <div key={resource.arn} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getTypeIcon(resource.type)}</span>
                          <span className="font-medium text-gray-900">{resource.name}</span>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(resource.status)}`}>
                          {resource.status}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>Type: {resource.type}</div>
                        {resource.function && <div>Function: {resource.function}</div>}
                        {resource.metrics?.invocations !== undefined && (
                          <div>Invocations: {resource.metrics.invocations}</div>
                        )}
                        {resource.metrics?.errors !== undefined && resource.metrics.errors > 0 && (
                          <div className="text-red-600">Errors: {resource.metrics.errors}</div>
                        )}
                        {resource.metrics?.lastExecution && (
                          <div>Last: {new Date(resource.metrics.lastExecution).toLocaleString()}</div>
                        )}
                      </div>
                      
                      {/* Tags */}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {Object.entries(resource.tags).slice(0, 3).map(([key, value]) => (
                          <span key={key} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            {key}: {value}
                          </span>
                        ))}
                        {Object.keys(resource.tags).length > 3 && (
                          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                            +{Object.keys(resource.tags).length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metrics
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {resourceGroups.flatMap(group => group.resources).map((resource) => (
                  <tr key={resource.arn} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-lg mr-3">{getTypeIcon(resource.type)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{resource.name}</div>
                          <div className="text-sm text-gray-500">{resource.function}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {resource.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {resource.workflowType || 'general'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(resource.status)}`}>
                        {resource.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {resource.metrics?.invocations !== undefined && (
                        <div>Invocations: {resource.metrics.invocations}</div>
                      )}
                      {resource.metrics?.errors !== undefined && resource.metrics.errors > 0 && (
                        <div className="text-red-600">Errors: {resource.metrics.errors}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowResources;
